import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Icon, Snackbar, Tab, Tabs, Tooltip } from '@mui/material';

import ProgressBar from '../../controls/ProgressBar';
import ErrorMessage from '../../controls/ErrorMessage';
import { Task } from '../../../models/portfolio/task';

import DialogTaskAdd from '../../controls/dialogs/DialogTaskAdd';

import { contentService } from '../../../services/content.service';
import { portfolioService } from '../../../services/portfolio.service';
import OverzichtTasks from './OverzichtTasks';
import { eventBus } from '../../../services/event-emitter.service';


interface TaskSwimlanesProps {
  portfolioId?: string;
  status?: string;
  mijnOrganisatieId?: string;
  organizations?: any[];
  refreshKey?: number;
  readOnly?: boolean;
}

const TaskSwimlanes: React.FC<TaskSwimlanesProps> = ({ 
  portfolioId,
  status,
  mijnOrganisatieId,
  organizations,
  refreshKey,
  readOnly = false,
}) => {

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});

  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [open, setOpenDialogs] = useState({
    task: false,
    taskShow: false,
    timeline: false,
    replies: false,
    snackbar: false
  });

  const [tabValue, setTabValue] = useState(0);
  const statusTabs = contentService.getTaskStatusses();

  useEffect(() => {
    const fetchStatusCounts = async () => {
      if (!portfolioId) {
        setStatusCounts({});
        return;
      }

      try {
        const results = await Promise.all(
          statusTabs.map(async (tab) => {
            const data = await portfolioService.getTasksByPortfolio(portfolioId, {
              offset: 0,
              limit: 1,
              filter: `status = '${tab.value}'`,
            } as any) as any;

            return [tab.value, data?.count ?? 0] as const;
          })
        );

        setStatusCounts(Object.fromEntries(results));
      }
      catch (error) {
        console.error('Fout bij ophalen van taakstatus-aantallen:', error);
      }
    };

    fetchStatusCounts();
  }, [portfolioId, refreshKey, statusTabs]);

  // Handlers to open/close each dialog
  const openDialog = useCallback((dialog: keyof typeof open) => {
    // setSelectedId(id);
    setOpenDialogs((prev) => ({ ...prev, [dialog]: true }));
  }, []);

  const closeDialog = useCallback((dialog: keyof typeof open) => {
    setOpenDialogs((prev) => ({ ...prev, [dialog]: false }));
  }, []);

  const triggerOpenDialog = useCallback((dialog: string) => {
    if (readOnly && dialog === 'task') {
      return;
    }

    if (dialog) {
      openDialog(dialog as keyof typeof open);
    }
  }, [readOnly, openDialog]);

  useEffect(() => {
    eventBus.on('openDialog', triggerOpenDialog);

    // Cleanup
    return () => {
      eventBus.off('openDialog', triggerOpenDialog);
    };
  }, [triggerOpenDialog]);
  
  const onClickAddTask = async () => {
    if (readOnly) {
      return;
    }
    openDialog('task');
  };

  const handleSubmitTask = async (task: Task) => {
    if (readOnly) {
      return;
    }

    try {
      setLoading(true);

      if (task) {

        let response;
        if (task.id) {
          response = await portfolioService.putTask(task);
        }
        else {
          response = await portfolioService.postTask(task);
        }
        
        if (response) { // response.ok
          // refreshTasks();
          setTimeout(() => {
            eventBus.emit('reloadTasks');

            setLoading(false);
            setSnackbarMessage('Actie succesvol opgeslagen');
            openDialog('snackbar');
          }, 2000);
        }
        else {
          setLoading(false);
        }
      } 
      else {
        console.error('Geen task om toe te voegen');
      }
      
    }
    catch (error) {
      setLoading(false);
      console.error('Error updating task:', error);
      setError('Er is een fout opgetreden');
    }
  };
  
  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={open.snackbar}
        autoHideDuration={2000}
        onClose={() => closeDialog('snackbar')}
        message={snackbarMessage ? snackbarMessage : 'Er is een fout opgetreden'}
        key={'topcenter'}
      />

      <div className="component-header">
        <h4>
          <Icon>assignment</Icon> Acties 
          {/* ({totalCount}) */}
        </h4>
        {!readOnly && (
          <div className="btn-group">
            <Tooltip title="Nieuwe actie toevoegen" placement="right" arrow>
              <Button variant="text" color="secondary" 
                onClick={() => onClickAddTask() }
              >
                <Icon>add</Icon>
                Actie
              </Button>
            </Tooltip>
          </div>
        )}
      </div>

      <Tabs
        value={tabValue}
        onChange={(e, value) => setTabValue(value)}
        aria-label="status tabs"
        TabIndicatorProps={{ className: `indicator-${tabValue}` }}
      >
        {statusTabs.map((tab, index) => (
          <Tab
            key={tab.value}
            label={(
              <Badge badgeContent={statusCounts[tab.value] ?? 0} color="primary" overlap="rectangular" sx={{ '& .MuiBadge-badge': { right: -14, top: 2 } }}>
                <span>{tab.label}</span>
              </Badge>
            )}
            icon={<Icon className={`icon-cirle ${tab.value}`}>{tab.icon}</Icon>}
            iconPosition="start"
            className={tab.value}
            sx={{ overflow: 'visible' }}
          />
        ))}
      </Tabs>

      {statusTabs.map((tab, index) => (
        tabValue === index && (
          <div key={tab.value}>
            {tabValue === index && portfolioId && mijnOrganisatieId && organizations && (
              <OverzichtTasks
                portfolioId={portfolioId} 
                status={statusTabs[tabValue].value}
                organizations={organizations} 
                mijnOrganisatieId={mijnOrganisatieId}
                readOnly={readOnly}
              />
            )}
          </div>
        )
      ))}

      <DialogTaskAdd
        open={open.task}
        portfolioId={portfolioId}
        organizations={organizations}
        onClose={ () => closeDialog('task') }
        onSubmit={handleSubmitTask}
      />

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage message={error} />
      )}
    </>
  );
};

export default TaskSwimlanes;
